/// @author Cyrill Tekord

export type ValueRetrieverCallback<FieldType = any, ResultType = any> =
    (field: Field<FieldType>) => ResultType;

export type ValueSetterCallback<FieldType = any, ValueType = any, ResultType = any> =
    (field: Field<FieldType>, value: ValueType) => ResultType;

export class Field<DataAttributeType = any> {
    name: string | null = null;
    placeholder: string | null = null;
    value: any | any[] = null;
    rules: any[] = [];
    items = [];
    errors: string[] = [];
    elementId: string | null = null;

    static make<T = any>(
        name: string,
        placeholder: string | null = null,
        initialValue: any = null,
    ) {
        let field = new Field<T>();
        field.name = name;
        field.placeholder = placeholder;
        field.value = initialValue;
        field.elementId = 'field-' + Math.random().toString(32).slice(2);

        return field;
    }

    data: DataAttributeType | null = null;

    hasData() {
        return Boolean(this.data).valueOf();
    }

    setData(value: DataAttributeType | null) {
        this.data = value;

        return this;
    }

    valueSetterCallback: ValueSetterCallback | null = null;

    setValueUsing(callback: ValueSetterCallback | null) {
        this.valueSetterCallback = callback;

        return this;
    }

    setValue(value) {
        this.value = this.valueSetterCallback
            ? this.valueSetterCallback(this, value)
            : value;

        return this;
    }

    addRules(...rules) {
        this.rules.push(...rules);

        return this;
    }

    clearRules() {
        this.rules = [];

        return this;
    }

    itemsGetterCallback: ValueRetrieverCallback<DataAttributeType, string> | null = null;

    getItemsUsing(callback: ValueRetrieverCallback<DataAttributeType, string> | null) {
        this.itemsGetterCallback = callback;

        return this;
    }

    getItems() {
        if (this.itemsGetterCallback)
            return this.itemsGetterCallback(this);

        return this.items;
    }

    setItems(values) {
        this.items = values;

        return this;
    }

    clearErrors() {
        this.errors = [];

        return this;
    }

    label: string | null = null;

    labelGetterCallback: ValueRetrieverCallback<DataAttributeType, string> | null = null;

    getLabelUsing(callback: ValueRetrieverCallback<DataAttributeType, string> | null) {
        this.labelGetterCallback = callback;

        return this;
    }

    getLabel() {
        if (this.labelGetterCallback)
            return this.labelGetterCallback(this);

        if (this.label)
            return this.label;

        if (this.placeholder)
            return this.placeholder;

        return this.name;
    }

    setLabel(value: string | null) {
        this.label = value;

        return this;
    }

    resetValueCallback: ValueRetrieverCallback | null = null;

    resetValueUsing(callback: ValueRetrieverCallback | null) {
        this.resetValueCallback = callback;

        return this;
    }

    reset() {
        if (this.resetValueCallback) {
            this.resetValueCallback(this);

            return;
        }

        if (Array.isArray(this.value)) {
            this.value.clear();
        }
        else {
            this.value = null;
        }
    }

    dumpValueCallback: ValueRetrieverCallback | null = null;

    dumpValueUsing(callback: ValueRetrieverCallback | null) {
        this.dumpValueCallback = callback;

        return this;
    }

    dumpValue() {
        return this.dumpValueCallback
            ? this.dumpValueCallback(this)
            : this.value;
    }
}
